import { Head, router } from '@inertiajs/react'
import { useState, useEffect } from 'react'
import Layout from '../../components/Layout'
import TopicTabNav from '../../components/topics/TopicTabNav'
import TopicFilterBar from '../../components/topics/TopicFilterBar'
import TopicSolvedFilterBar from '../../components/topics/TopicSolvedFilterBar'
import TopicList from '../../components/topics/TopicList'
import ViewToggle from '../../components/topics/ViewToggle'
import type { TopicView } from '../../components/topics/ViewToggle'
import TopicModal from '../../components/topics/TopicModal'
import TopicResolveModal from '../../components/topics/TopicResolveModal'
import TopicEmptyState from '../../components/topics/TopicEmptyState'
import ActionItemModal from '../../components/meetings/ActionItemModal'
import type { Topic, TopicFilters, TabCounts, User } from '../../types'

interface Props {
  team: { id: number; name: string; description: string | null; member_count: number }
  topics: Topic[]
  tab_counts: TabCounts
  filters: TopicFilters
  current_meeting: { id: number; status: string } | null
  team_members: User[]
  can_create: boolean
  can_reorder: boolean
  active_tab: string
}

export default function Topics({
  team, topics, tab_counts, filters, current_meeting,
  team_members, can_create, can_reorder, active_tab
}: Props) {
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingTopic, setEditingTopic] = useState<Topic | null>(null)
  const [resolvingTopic, setResolvingTopic] = useState<Topic | null>(null)
  const [convertingTopic, setConvertingTopic] = useState<Topic | null>(null)
  const [visibleTopics, setVisibleTopics] = useState(topics)
  const [manual, setManual] = useState(false)
  useEffect(() => { setManual(false) }, [filters.sort])
  const counts = { ...tab_counts }
  for (const type of ['short_term', 'long_term'] as const) {
    counts[type] = (counts[type] ?? 0) + visibleTopics.filter(t => t.topic_type === type).length - topics.filter(t => t.topic_type === type).length
  }

  const applyFilters = (newFilters: Partial<TopicFilters>) => {
    if (newFilters.sort) setManual(newFilters.sort === 'manual')
    router.get(`/teams/${team.id}/topics`, {
      ...filters, ...(manual ? { sort: 'manual' } : {}),
      ...newFilters
    } as any, {
      preserveState: true,
      preserveScroll: true
    })
  }

  const handleConvert = (topic: Topic) => {
    setConvertingTopic(topic)
  }

  const view: TopicView = filters.view === 'grid' ? 'grid' : 'list'

  return (
    <Layout team={team} activeTab={active_tab}>
      <Head title={`Topics - ${team.name}`} />

      <div className="max-w-screen-2xl mx-auto px-6 py-6">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-semibold text-text-primary">The List of Topics</h1>
          <div className="flex items-center gap-3">
            <ViewToggle view={view} onChange={(v) => applyFilters({ view: v })} />
            {can_create && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="bg-pinnacle-red text-white px-5 py-2.5 rounded-md font-semibold text-base hover:bg-pinnacle-red-dark transition-colors flex items-center gap-1.5"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Create
              </button>
            )}
          </div>
        </div>

        <TopicTabNav
          activeTab={filters.tab}
          counts={tab_counts}
          onTabChange={(tab) => applyFilters({ tab, search: null, category: null, sort: 'manual', solved_range: 'all_time', topic_type: null })}
        />

        {filters.tab === 'solved' ? (
          <TopicSolvedFilterBar
            filters={filters}
            onFilterChange={applyFilters}
          />
        ) : (
          <TopicFilterBar
            filters={manual ? { ...filters, sort: 'manual' } : filters}
            counts={counts}
            onFilterChange={applyFilters}
          />
        )}

        {topics.length > 0 || (filters.tab !== 'solved' && view === 'list') ? (
          <TopicList
            topics={topics}
            teamId={team.id}
            activeTab={filters.tab}
            canReorder={can_reorder && filters.tab !== 'solved'}
            filters={filters}
            onRowsChange={setVisibleTopics}
            onManual={() => setManual(true)}
            view={view}
            onEdit={(topic) => setEditingTopic(topic)}
            onResolve={(topic) => setResolvingTopic(topic)}
            onConvert={handleConvert}
          />
        ) : (
          <TopicEmptyState
            tab={filters.tab}
            onCreateClick={() => setShowCreateModal(true)}
          />
        )}
      </div>

      {showCreateModal && (
        <TopicModal
          teamId={team.id}
          teamMembers={team_members}
          filters={filters}
          onClose={() => setShowCreateModal(false)}
        />
      )}

      {editingTopic && (
        // Keyed on the topic id so picking a different topic while the panel
        // is open re-initializes the form with that topic's values, while
        // re-renders for the same topic never reset a mid-edit form.
        <TopicModal
          key={editingTopic.id}
          teamId={team.id}
          teamMembers={team_members}
          topic={editingTopic}
          filters={filters}
          onClose={() => setEditingTopic(null)}
          // Solved / Convert open a modal ABOVE the panel; the panel stays
          // mounted so cancelling either one leaves the edit draft intact. It
          // closes only once the resolve/convert actually succeeds.
          onResolve={() => setResolvingTopic(editingTopic)}
          onConvert={(draft) => {
            // Merge the in-progress edit-form values over the saved topic so
            // unsaved title/description edits survive into the conversion modal.
            handleConvert({ ...editingTopic, ...draft })
          }}
        />
      )}

      {convertingTopic && (
        <ActionItemModal
          isOpen={!!convertingTopic}
          onClose={() => setConvertingTopic(null)}
          teamMembers={team_members}
          defaultTitle={convertingTopic.title}
          defaultDescription={convertingTopic.description || ''}
          convertUrl={`/teams/${team.id}/topics/${convertingTopic.id}/convert_to_action`}
          convertExtraData={{ resolve_topic: true }}
          onConverted={() => setEditingTopic(null)}
        />
      )}

      {resolvingTopic && (
        <TopicResolveModal
          topic={resolvingTopic}
          teamId={team.id}
          teamMembers={team_members}
          meetingId={current_meeting?.id}
          onClose={() => setResolvingTopic(null)}
          onResolved={() => setEditingTopic(null)}
        />
      )}
    </Layout>
  )
}
